import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  createOrder,
  verifyPayment,
  saveProfile,
  startGoogleSignIn,
} from "@/lib/api";
import { openRazorpayCheckout, preloadRazorpay } from "@/lib/razorpay";
import CountryPicker from "@/components/CountryPicker";
import { formatPrice } from "@/lib/pricing";
import { Loader2, CheckCircle2 } from "lucide-react";

const STAGES = {
  REGION: "region",
  LOGIN: "login",
  TIER: "tier",
  PROCESSING: "processing",
  PROFILE: "profile",
  DONE: "done",
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.3l-6.3-5.2C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.3 5.2C41.3 35.3 44 30 44 24c0-1.2-.1-2.3-.4-3.5z" />
  </svg>
);

const pickInitialStage = (auth, pricing) => {
  // If we don't have a price yet, the visitor must pick their region first.
  if (!pricing || pricing.price == null) return STAGES.REGION;
  if (!auth?.signedIn) return STAGES.LOGIN;
  if (!auth.hasAccess) return STAGES.TIER;
  if (!auth.profileComplete) return STAGES.PROFILE;
  return STAGES.DONE;
};

export default function PaywallDialog({
  open,
  onOpenChange,
  pricing,
  onPricingChange,
  auth,
  onComplete,
}) {
  const [stage, setStage] = useState(() => pickInitialStage(auth, pricing));

  // Profile form fields — prefill from existing user data if available.
  const [pname, setPname] = useState("");
  const [pcity, setPcity] = useState("");
  const [pphone, setPphone] = useState("");
  const profileInitialised = useRef(false);

  // Sync stage when the dialog (re)opens or auth/pricing state changes meaningfully.
  useEffect(() => {
    if (!open) return;
    const next = pickInitialStage(auth, pricing);
    setStage(next);
    if (auth && !profileInitialised.current) {
      setPname(auth.name || "");
      setPcity(auth.city || "");
      setPphone(auth.phone || "");
      profileInitialised.current = true;
    }
    if (next === STAGES.TIER) preloadRazorpay();
  }, [open, auth?.signedIn, auth?.hasAccess, auth?.profileComplete, pricing?.price, pricing?.region]);

  const handleClose = (v) => {
    if (!v) profileInitialised.current = false;
    onOpenChange(v);
  };

  const handleSignIn = () => startGoogleSignIn("/watch");

  // --- TIER STAGE ---
  const price = pricing?.price ?? 2.99;
  const currency = pricing?.currency || "USD";
  const accessDays = pricing?.access_days ?? 60;

  const handlePay = async () => {
    setStage(STAGES.PROCESSING);
    try {
      const order = await createOrder(price, currency);
      let checkoutResult;
      try {
        checkoutResult = await openRazorpayCheckout({
          orderId: order.order_id,
          amount: order.amount,
          currency: order.currency,
          email: order.email,
          description: `Byo Cha Khape · ${accessDays}-day access (${formatPrice(price, currency)})`,
        });
      } catch (err) {
        if (err?.message === "dismissed") {
          setStage(STAGES.TIER);
          return;
        }
        throw err;
      }
      await verifyPayment({
        razorpay_order_id: checkoutResult.razorpay_order_id,
        razorpay_payment_id: checkoutResult.razorpay_payment_id,
        razorpay_signature: checkoutResult.razorpay_signature,
      });
      setStage(STAGES.PROFILE);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Payment failed. Please try again.");
      setStage(STAGES.TIER);
    }
  };

  // --- PROFILE STAGE ---
  const isValidName = pname.trim().length >= 2;
  const isValidCity = pcity.trim().length >= 2;
  const isValidPhone = pphone.trim().replace(/\D/g, "").length >= 7;
  const [profileSaving, setProfileSaving] = useState(false);

  const handleSaveProfile = async () => {
    if (!isValidName) return toast.error("Please enter your full name");
    if (!isValidCity) return toast.error("Please enter your city");
    if (!isValidPhone) return toast.error("Please enter a valid phone number");
    setProfileSaving(true);
    try {
      await saveProfile({ name: pname.trim(), city: pcity.trim(), phone: pphone.trim() });
      setStage(STAGES.DONE);
      setTimeout(() => {
        onOpenChange(false);
        if (onComplete) onComplete();
      }, 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="paywall-dialog"
        className="bg-[#0A0A0A] border border-[#27272A] rounded-none w-[calc(100%-1rem)] max-w-lg p-0 text-[#F3F3F2] max-h-[92vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8 md:p-10">
          {stage === STAGES.REGION && (
            <div data-testid="paywall-region">
              <DialogHeader className="text-left space-y-2 sm:space-y-3 mb-6">
                <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase font-body">
                  Step 1 · Your country
                </div>
                <DialogTitle className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight">
                  Where are you watching from?
                </DialogTitle>
                <DialogDescription className="font-body text-[#A1A1AA] text-sm sm:text-base">
                  Pick your country to continue. We&rsquo;ll show you the price on the next step.
                </DialogDescription>
              </DialogHeader>

              <div className="mb-2">
                <CountryPicker
                  countryCode={pricing?.country_code}
                  onChange={(next) => onPricingChange && onPricingChange(next)}
                  placeholder="Select Your Country"
                />
              </div>
            </div>
          )}

          {stage === STAGES.LOGIN && (
            <div data-testid="paywall-login">
              <DialogHeader className="text-left space-y-2 sm:space-y-3 mb-6">
                <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase font-body">
                  Step 1 · Sign in
                </div>
                <DialogTitle className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight">
                  Continue with Google to buy your ticket.
                </DialogTitle>
                <DialogDescription className="font-body text-[#A1A1AA] text-sm sm:text-base">
                  We use your Google email to lock the film to your account so only you can watch — no passwords, no spam.
                </DialogDescription>
              </DialogHeader>
              <button
                data-testid="paywall-google-btn"
                onClick={handleSignIn}
                className="w-full py-4 bg-[#F3F3F2] text-[#0A0A0A] hover:bg-white transition-colors duration-300 text-sm tracking-[0.18em] uppercase font-semibold inline-flex items-center justify-center gap-3"
              >
                <GoogleIcon />
                Continue with Google
              </button>
              <div className="mt-5 text-center text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#52525B] font-body">
                We&rsquo;ll show you the plan right after.
              </div>
            </div>
          )}

          {stage === STAGES.TIER && (
            <>
              <DialogHeader className="text-left space-y-2 sm:space-y-3 mb-5 sm:mb-7">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase font-body">
                    Step 2 · Your ticket
                  </div>
                  <CountryPicker
                    compact
                    countryCode={pricing?.country_code}
                    onChange={(next) => onPricingChange && onPricingChange(next)}
                  />
                </div>
                <DialogTitle className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight">
                  One price. {accessDays} days. The whole film.
                </DialogTitle>
                <DialogDescription className="font-body text-[#A1A1AA] text-sm sm:text-base">
                  Pay once, watch as many times as you like for {accessDays} days. No subscriptions, no auto-renew.
                </DialogDescription>
              </DialogHeader>

              {auth?.email && (
                <div
                  data-testid="paywall-signed-in-as"
                  className="mb-5 sm:mb-6 text-[10px] sm:text-xs tracking-[0.18em] uppercase text-[#A1A1AA] font-body border border-[#27272A] bg-[#141414] px-4 py-3 flex items-center justify-between"
                >
                  <span>Signed in as</span>
                  <span className="text-[#F3F3F2] tracking-normal text-xs normal-case truncate ml-2">{auth.email}</span>
                </div>
              )}

              {/* Single ticket card */}
              <div
                data-testid="paywall-tier-card"
                className="border border-[#D4A373] bg-[#D4A373]/5 p-6 sm:p-7 flex items-center justify-between"
              >
                <div>
                  <div className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#D4A373]/80 font-body mb-1.5">
                    {accessDays}-Day Access
                  </div>
                  <div className="font-body text-[#F3F3F2] text-sm">Full film · subtitles · HD</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-4xl sm:text-5xl leading-none text-[#D4A373]">
                    {formatPrice(price, currency)}
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[#A1A1AA] font-body mt-1">
                    one-time
                  </div>
                </div>
              </div>

              <button
                data-testid="paywall-pay-btn"
                onClick={handlePay}
                className="w-full mt-6 py-4 bg-[#D4A373] text-[#0A0A0A] hover:bg-[#E5B98D] transition-all duration-300 text-sm tracking-[0.2em] uppercase font-semibold"
              >
                Pay {formatPrice(price, currency)} & Unlock
              </button>

              <div className="mt-4 text-center text-xs tracking-[0.15em] uppercase text-[#52525B] font-body">
                Secure payment by Razorpay
              </div>
            </>
          )}

          {stage === STAGES.PROCESSING && (
            <div className="py-16 flex flex-col items-center justify-center text-center" data-testid="paywall-processing">
              <Loader2 className="w-10 h-10 text-[#D4A373] animate-spin mb-6" />
              <div className="font-display text-2xl mb-2">Processing your payment…</div>
              <div className="font-body text-sm text-[#A1A1AA]">Securing your seat in the theatre.</div>
            </div>
          )}

          {stage === STAGES.PROFILE && (
            <div data-testid="paywall-profile">
              <DialogHeader className="text-left space-y-2 sm:space-y-3 mb-6">
                <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase font-body">
                  Final step · Your details
                </div>
                <DialogTitle className="font-display text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight">
                  Tell us where to send postcards.
                </DialogTitle>
                <DialogDescription className="font-body text-[#A1A1AA] text-sm sm:text-base">
                  Just three quick fields and the film opens.
                </DialogDescription>
              </DialogHeader>

              <label className="block text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-body mb-2">
                Full Name
              </label>
              <Input
                data-testid="paywall-profile-name"
                type="text"
                autoComplete="name"
                value={pname}
                onChange={(e) => setPname(e.target.value)}
                placeholder="Asha Khatri"
                className="bg-[#141414] border-[#27272A] rounded-none focus:border-[#D4A373] focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base text-[#F3F3F2] placeholder:text-[#52525B]"
              />

              <label className="block text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-body mb-2 mt-4">
                City
              </label>
              <Input
                data-testid="paywall-profile-city"
                type="text"
                autoComplete="address-level2"
                value={pcity}
                onChange={(e) => setPcity(e.target.value)}
                placeholder="Mumbai, Dubai, Hyderabad..."
                className="bg-[#141414] border-[#27272A] rounded-none focus:border-[#D4A373] focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base text-[#F3F3F2] placeholder:text-[#52525B]"
              />

              <label className="block text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-body mb-2 mt-4">
                Phone Number
              </label>
              <Input
                data-testid="paywall-profile-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={pphone}
                onChange={(e) => setPphone(e.target.value)}
                placeholder="+91 98765 43210"
                className="bg-[#141414] border-[#27272A] rounded-none focus:border-[#D4A373] focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base text-[#F3F3F2] placeholder:text-[#52525B]"
              />

              <button
                data-testid="paywall-profile-submit"
                onClick={handleSaveProfile}
                disabled={!isValidName || !isValidCity || !isValidPhone || profileSaving}
                className="w-full mt-7 py-4 bg-[#D4A373] text-[#0A0A0A] hover:bg-[#E5B98D] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 text-sm tracking-[0.2em] uppercase font-semibold inline-flex items-center justify-center gap-3"
              >
                {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock The Film"}
              </button>
            </div>
          )}

          {stage === STAGES.DONE && (
            <div className="py-16 flex flex-col items-center justify-center text-center" data-testid="paywall-success">
              <CheckCircle2 className="w-10 h-10 text-[#D4A373] mb-6" />
              <div className="font-display text-3xl mb-2">You&rsquo;re in.</div>
              <div className="font-body text-sm text-[#A1A1AA]">Opening the film…</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


